import { collection, doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { db } from '../config/firebase';
import { UserProfile } from '../types';

export class UserService {
  private static readonly COLLECTION_NAME = 'userProfiles';
  
  // Admin user UID - Phil's account
  private static readonly ADMIN_UID = 'bv0UEU20wKXCwU3q1hSIfGYv3nk1';
  
  // Getter for admin UID
  static get AdminUID(): string {
    return this.ADMIN_UID;
  }
  
  /**
   * Create a user profile for a new user
   */
  static async createUserProfile(user: User): Promise<void> {
    const existingProfile = await this.getUserProfile(user.uid);
    if (existingProfile) {
      return; // Profile already exists
    }
    
    const userProfile: Omit<UserProfile, 'createdAt' | 'updatedAt'> = {
      uid: user.uid,
      email: user.email || '',
      isAdmin: user.uid === this.ADMIN_UID,
    };
    
    // Only add displayName if it exists
    if (user.displayName) {
      (userProfile as any).displayName = user.displayName;
    }
    
    await setDoc(doc(db, this.COLLECTION_NAME, user.uid), {
      ...userProfile,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }
  
  /**
   * Get a user's profile
   */
  static async getUserProfile(uid: string): Promise<UserProfile | null> {
    const docRef = doc(db, this.COLLECTION_NAME, uid);
    const docSnap = await getDoc(docRef);
    
    if (!docSnap.exists()) {
      return null;
    }
    
    const data = docSnap.data();
    return {
      uid: docSnap.id,
      email: data.email,
      displayName: data.displayName,
      isAdmin: data.isAdmin || false,
      createdAt: data.createdAt?.toDate() || new Date(),
      updatedAt: data.updatedAt?.toDate() || new Date(),
      handle: data.handle,
      profilePicUrl: data.profilePicUrl,
      bio: data.bio,
      preferences: data.preferences,
      sharingPreferences: data.sharingPreferences,
      socialStats: data.socialStats,
      watchlistImportHistory: data.watchlistImportHistory ? {
        lastImportedFrom: data.watchlistImportHistory.lastImportedFrom,
        lastImportDate: data.watchlistImportHistory.lastImportDate?.toDate(),
        importedSymbols: data.watchlistImportHistory.importedSymbols,
      } : undefined,
    };
  }
  
  /**
   * Update a user's profile
   */
  static async updateUserProfile(uid: string, updates: Partial<UserProfile>): Promise<void> {
    const docRef = doc(db, this.COLLECTION_NAME, uid);
    
    // Don't allow updating uid or isAdmin through this method
    const { uid: _, isAdmin, ...safeUpdates } = updates;
    
    // Prepare the update object
    const updateData: any = {
      updatedAt: serverTimestamp(),
    };
    
    // Handle nested objects properly
    Object.entries(safeUpdates).forEach(([key, value]) => {
      if (key === 'sharingPreferences' && typeof value === 'object') {
        // Handle nested sharingPreferences object
        Object.entries(value).forEach(([subKey, subValue]) => {
          updateData[`sharingPreferences.${subKey}`] = subValue;
        });
      } else {
        updateData[key] = value;
      }
    });
    
    await updateDoc(docRef, updateData);
  }
  
  /**
   * Check if a user is an admin
   */
  static async isAdmin(uid: string): Promise<boolean> {
    // Quick check for known admin
    if (uid === this.ADMIN_UID) {
      return true;
    }
    
    const profile = await this.getUserProfile(uid);
    return profile?.isAdmin || false;
  }
  
  /**
   * Update user's display name
   */
  static async updateDisplayName(uid: string, displayName: string): Promise<void> {
    await this.updateUserProfile(uid, { displayName });
  }
  
  /**
   * Record a watchlist import event
   */
  static async recordWatchlistImport(uid: string, sourceUserId: string, symbols: string[]): Promise<void> {
    const docRef = doc(db, this.COLLECTION_NAME, uid);
    
    await updateDoc(docRef, {
      'watchlistImportHistory.lastImportedFrom': sourceUserId,
      'watchlistImportHistory.lastImportDate': serverTimestamp(),
      'watchlistImportHistory.importedSymbols': symbols,
      updatedAt: serverTimestamp(),
    });
  }
  
  /**
   * Get the last import info for a user
   */
  static async getWatchlistImportHistory(uid: string): Promise<UserProfile['watchlistImportHistory'] | null> {
    const profile = await this.getUserProfile(uid);
    return profile?.watchlistImportHistory || null;
  }
}